/* eslint-disable react/style-prop-object */
import "./index.css";
import "./Component/tablecomponents.css";
import "../Product/index.css";
import "../CompanyDetail2/index.css";
import "../CompanyDetail/index.css";
import { useState, useEffect, useCallback, createContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { deleteProduct } from "../../apis/Product";
import { AlertV2 } from "../../components/Alert";
import { useTranslation } from "react-i18next";
import { ContainerBody } from "../../App";
import { getProductSummary } from "../../apis/ARDash/ProductSummary";
import { NewProductDetail } from "../NewProductDetail";
import { ProductDetailView as ProductDetailViewPCF } from "../NewProductCategories/ProductDetails";
import { ProductDetailViewNew as ProductDetailViewLCANew } from "../NewProductCategories/ProductDetailsLCA/ProductDetailViewNew";
import {
  LovableDeleteProductButton,
  LovableGeneratePdfButton,
} from "../NewProductCategories/ProductDetailsLCA/components/LovableFlowControls";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../AdminDash/components/ui/tabs";
import "../NewProductCategories/ProductDetailsLCA/components/LovableFlowControls/index.css";
import "../NewProductDetail/product-detail-pcf-theme.css";
import YFSLabelProductDetail from "../NewProductDetail/YFSLabelProductDetail";
import Sidebar from "../NewProductCategories/Sidebar";
import { getProductDetail } from "../../apis/Product";
import {
  GeneratePagePDFProduct,
  GeneratePagePDFProductAlternative,
} from "../../components/GeneratePDF/GeneratePDFProduct";
import { useBonsaiPreload } from "../../hooks/useBonsaiData";
import { useProductCatalogTabContext } from "../Product/ProductCatalogTabContext";

export const DropdownInventoriesContext = createContext();

const ProductDetail = () => {
  const [showCategory, setShowCategory] = useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // Get productId and business from URL params first
  let { productId } = useParams();
  const business = queryParams.get("business");

  // Get assessment_type from URL first, then use API response
  const assessmentTypeFromUrl = queryParams.get("assessment_type");

  const [pcfTab, setPcfTab] = useState("environment");
  const [productSummaryData, setProductSummaryData] = useState([]);
  const [deleteProductAlert, setDeleteProductAlert] = useState(false);
  // Initialize with URL parameter or default to PCF
  const [assessmentType, setAssessmentType] = useState(
    assessmentTypeFromUrl || "PCF",
  );

  // Preload Bonsai data as soon as user enters product section
  useBonsaiPreload();

  // Fetch product assessment type
  useEffect(() => {
    if (productId) {
      getProductDetail({
        id: productId,
        business: business,
        setAssessmentType: (type) => {
          // Use API response if available, otherwise keep URL param or default to PCF
          setAssessmentType(type || assessmentTypeFromUrl || "PCF");
        },
      });
    }
  }, [productId, business, assessmentTypeFromUrl]);

  // PDF generation states
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [pdfProgress, setPdfProgress] = useState("");
  const [pdfMethod, setPdfMethod] = useState("alternative"); // "optimized" or "alternative"

  useEffect(() => {
    if (window.location.href.includes("product")) {
      getProductSummary({
        id: productId,
        business: business,
        setObjects: setProductSummaryData,
      });
    }
  }, [business, productId]);

  const { t } = useTranslation();
  const catalogTabCtx = useProductCatalogTabContext();

  const handleDelete = () => {
    setDeleteProductAlert(true);
  };

  const navigate = useNavigate();
  const handleMove = useCallback(() => {
    if (catalogTabCtx?.goCatalog) {
      catalogTabCtx.goCatalog();
      return;
    }
    navigate("/product", { replace: true });
  }, [navigate, catalogTabCtx]);

  // Enhanced PDF Generation Handler
  const handleGeneratePDF = async () => {
    setIsGeneratingPDF(true);
    setPdfProgress("Initializing PDF generation...");

    try {
      const generateFunction =
        pdfMethod === "alternative"
          ? GeneratePagePDFProductAlternative
          : GeneratePagePDFProduct;

      const result = await generateFunction({
        filename: `product-detail-${productId}-${
          new Date().toISOString().split("T")[0]
        }.pdf`,
        onProgress: setPdfProgress,
        quality: 0.8,
        format: "a4",
        maxHeight: 2500,
      });

      if (result.success) {
        setPdfProgress(
          `PDF generated successfully! (${result.pageCount} pages)`,
        );
        setTimeout(() => {
          setPdfProgress("");
          setIsGeneratingPDF(false);
        }, 3000);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error("PDF generation error:", error);

      if (pdfMethod === "optimized") {
        setPdfProgress("Retrying with alternative method...");
        setPdfMethod("alternative");

        try {
          const result = await GeneratePagePDFProductAlternative({
            filename: `product-detail-${productId}-${
              new Date().toISOString().split("T")[0]
            }.pdf`,
            onProgress: setPdfProgress,
            quality: 0.6,
            format: "a4",
          });

          if (result.success) {
            setPdfProgress(
              `PDF generated successfully! (${result.pageCount} pages)`,
            );
            setTimeout(() => {
              setPdfProgress("");
              setIsGeneratingPDF(false);
            }, 3000);
            return;
          }
        } catch (alternativeError) {
          console.error(
            "Alternative PDF generation also failed:",
            alternativeError,
          );
        }
      }

      setPdfProgress(
        "Failed to generate PDF. Please try refreshing the page and trying again.",
      );
      setTimeout(() => {
        setPdfProgress("");
        setIsGeneratingPDF(false);
      }, 5000);
    }
  };

  return (
    <>
      <Sidebar />
      <ContainerBody
        className="product__body"
        style={
          assessmentType === "LCA"
            ? { paddingLeft: "8px", paddingBottom: "20px" }
            : undefined
        }
      >
        {assessmentType === "LCA" ? (
          <ProductDetailViewLCANew
            showCategory={showCategory}
            setShowCategory={setShowCategory}
          />
        ) : (
          <>
            <NewProductDetail showCategory={showCategory} hideDelete />

            <Tabs
              value={pcfTab}
              onValueChange={setPcfTab}
              className="product-detail-view-new-esg-tabs"
              style={{ marginTop: "16px" }}
            >
              <TabsList
                style={{
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                }}
              >
                <TabsTrigger value="environment">E – Environment</TabsTrigger>
                <TabsTrigger value="social">S – Social</TabsTrigger>
                <TabsTrigger value="governance">G – Governance</TabsTrigger>
              </TabsList>

              <TabsContent value="environment">
                <ProductDetailViewPCF
                  showCategory={showCategory}
                  setShowCategory={setShowCategory}
                />
                <div style={{ paddingRight: "30px" }}>
                  <YFSLabelProductDetail productData={productSummaryData} />
                </div>
              </TabsContent>

              <TabsContent value="social">
                <div className="pcf-esg-placeholder">
                  <p>
                    In this section we dive into everything there is concerning
                    people in your organisation and from your suppliers towards
                    your clients. Starting with basic data employees.
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="governance">
                <div className="pcf-esg-placeholder">
                  <p>
                    The purpose of this section is to dive into where the company
                    in the governance structure can support everything from
                    prevention of whitewash scandals to diversity in the board and
                    management.
                  </p>
                </div>
              </TabsContent>
            </Tabs>

            <div className="lovable-page-footer">
              <LovableGeneratePdfButton
                onClick={handleGeneratePDF}
                isGenerating={isGeneratingPDF}
              />
              {pdfProgress && (
                <p
                  className={[
                    "lovable-page-footer__status",
                    pdfProgress.includes("Failed")
                      ? "lovable-page-footer__status--error"
                      : pdfProgress.includes("successfully")
                        ? "lovable-page-footer__status--success"
                        : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {pdfProgress}
                </p>
              )}
            </div>

            <div className="productdetail__wrapper productdetail__wrapper--lovable">
              <LovableDeleteProductButton
                label={t("DELETE_PRODUCT")}
                onClick={handleDelete}
              />
            </div>
          </>
        )}

        {assessmentType === "LCA" && (
          <div className="productdetail__wrapper productdetail__wrapper--lovable">
            <LovableDeleteProductButton
              label={t("DELETE_PRODUCT")}
              onClick={handleDelete}
            />
          </div>
        )}
      </ContainerBody>

      <AlertV2
        open={deleteProductAlert}
        title={t("DELETE_PRODUCT_2")}
        accept={() => {
          deleteProduct({ id: productId, business: business });
          handleMove();
        }}
        decline={() => setDeleteProductAlert(false)}
      />

      {/* PDF Generation Overlay */}
      {isGeneratingPDF && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "8px",
              textAlign: "center",
              maxWidth: "400px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #007bff",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px",
              }}
            />
            <h4 style={{ marginBottom: "10px", color: "#333" }}>
              Generating PDF...
            </h4>
            <p style={{ color: "#666", fontSize: "14px" }}>
              {pdfProgress || "Please wait while we prepare your PDF"}
            </p>
          </div>
        </div>
      )}

      {/* Add spinning animation CSS */}
      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </>
  );
};
export default ProductDetail;
